import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { TemplateCategoriesService, TemplateCategory, CategoryAssignmentData } from '../template-categories.service';

export interface CategoryAssignmentDialogData {
    templateId: number;
    templateType: 'oficial' | 'cerere';
    templateName: string;
    currentCategories?: TemplateCategory[];
    currentPrimaryCategory?: TemplateCategory;
    directieId?: number;
    compartimentId?: number;
}

@Component({
    selector: 'app-category-assignment-dialog',
    templateUrl: './category-assignment-dialog.component.html',
    styleUrls: ['./category-assignment-dialog.component.scss']
})
export class CategoryAssignmentDialogComponent implements OnInit {
    assignmentForm: FormGroup;
    isLoading = false;
    isSubmitting = false;

    // Category data
    availableCategories: TemplateCategory[] = [];
    filteredCategories!: Observable<TemplateCategory[]>;
    selectedCategories: TemplateCategory[] = [];
    primaryCategory: TemplateCategory | null = null;

    // Tree view data
    showTreeView = false;
    categoryTree: TemplateCategory[] = [];

    // Form validation errors
    validationErrors: string[] = [];

    // Assignment mode
    assignmentMode: 'replace' | 'add' = 'add';

    constructor(
        private fb: FormBuilder,
        private dialogRef: MatDialogRef<CategoryAssignmentDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: CategoryAssignmentDialogData,
        private templateCategoriesService: TemplateCategoriesService
    ) {
        this.assignmentForm = this.createForm();
    }

    ngOnInit(): void {
        this.loadAvailableCategories();
        this.setupCategoryFilter();
        this.initializeCurrentAssignments();
    }

    createForm(): FormGroup {
        return this.fb.group({
            category_search: [''],
            set_primary: [false],
            replace_existing: [false]
        });
    }

    initializeCurrentAssignments(): void {
        if (this.data.currentCategories) {
            this.selectedCategories = [...this.data.currentCategories];
        }

        if (this.data.currentPrimaryCategory) {
            this.primaryCategory = this.data.currentPrimaryCategory;
            this.assignmentForm.patchValue({ set_primary: true });
        }

        this.assignmentMode = this.assignmentForm.get('replace_existing')?.value ? 'replace' : 'add';
    }

    loadAvailableCategories(): void {
        this.isLoading = true;

        const directieId = this.data.directieId;
        const compartimentId = this.data.compartimentId;

        this.templateCategoriesService.getCategoriesByOrganization(directieId || 1, compartimentId)
            .subscribe({
                next: (response) => {
                    this.isLoading = false;
                    if (response.errors === false) {
                        this.availableCategories = response.data || [];
                        this.loadCategoryTree();
                    }
                },
                error: (error) => {
                    this.isLoading = false;
                    console.error('Error loading categories:', error);
                    this.availableCategories = [];
                }
            });
    }

    loadCategoryTree(): void {
        const directieId = this.data.directieId;
        const compartimentId = this.data.compartimentId;

        this.templateCategoriesService.getTreeStructure(directieId, compartimentId)
            .subscribe({
                next: (response) => {
                    if (response.errors === false) {
                        this.categoryTree = response.data || [];
                    }
                },
                error: (error) => {
                    console.error('Error loading category tree:', error);
                    this.categoryTree = [];
                }
            });
    }

    setupCategoryFilter(): void {
        this.filteredCategories = this.assignmentForm.get('category_search')!.valueChanges.pipe(
            startWith(''),
            map(value => {
                const filterValue = typeof value === 'string' ? value.toLowerCase() : '';
                return this.availableCategories.filter(category =>
                    category.name.toLowerCase().includes(filterValue) ||
                    (category.description && category.description.toLowerCase().includes(filterValue))
                );
            })
        );
    }

    displayCategory(category: TemplateCategory): string {
        return category ? category.name : '';
    }

    onCategorySelected(category: TemplateCategory): void {
        if (!this.isCategorySelected(category)) {
            this.selectedCategories.push(category);
            this.assignmentForm.get('category_search')?.setValue('');
        }
    }

    onCategoryTreeSelected(category: TemplateCategory): void {
        this.onCategorySelected(category);
    }

    onCategoryTreeMultiSelected(categories: TemplateCategory[]): void {
        // Replace current selection with tree selection
        this.selectedCategories = [...categories];
    }

    removeCategory(category: TemplateCategory): void {
        const index = this.selectedCategories.findIndex(c => c.id === category.id);
        if (index > -1) {
            this.selectedCategories.splice(index, 1);

            // If this was the primary category, clear it
            if (this.primaryCategory && this.primaryCategory.id === category.id) {
                this.primaryCategory = null;
                this.assignmentForm.patchValue({ set_primary: false });
            }
        }
    }

    setPrimaryCategory(category: TemplateCategory): void {
        this.primaryCategory = category;
        this.assignmentForm.patchValue({ set_primary: true });
    }

    clearPrimaryCategory(): void {
        this.primaryCategory = null;
        this.assignmentForm.patchValue({ set_primary: false });
    }

    isCategorySelected(category: TemplateCategory): boolean {
        return this.selectedCategories.some(c => c.id === category.id);
    }

    isPrimaryCategory(category: TemplateCategory): boolean {
        return this.primaryCategory ? this.primaryCategory.id === category.id : false;
    }

    toggleTreeView(): void {
        this.showTreeView = !this.showTreeView;
    }

    onAssignmentModeChange(): void {
        this.assignmentMode = this.assignmentForm.get('replace_existing')?.value ? 'replace' : 'add';
    }

    validateAssignment(): boolean {
        this.validationErrors = [];

        if (this.selectedCategories.length === 0) {
            this.validationErrors.push('Trebuie să selectați cel puțin o categorie.');
            return false;
        }

        // If set_primary is true, make sure we have a primary category
        if (this.assignmentForm.get('set_primary')?.value && !this.primaryCategory) {
            this.validationErrors.push('Trebuie să selectați o categorie primară.');
            return false;
        }

        // Make sure primary category is in selected categories
        if (this.primaryCategory && !this.isCategorySelected(this.primaryCategory)) {
            this.validationErrors.push('Categoria primară trebuie să fie inclusă în categoriile selectate.');
            return false;
        }

        return true;
    }

    onSubmit(): void {
        if (!this.validateAssignment()) {
            return;
        }

        this.isSubmitting = true;

        const assignmentData: CategoryAssignmentData = {
            template_id: this.data.templateId,
            template_type: this.data.templateType,
            category_ids: this.selectedCategories.map(c => c.id),
            set_primary: this.assignmentForm.get('set_primary')?.value || false,
            primary_category_id: this.primaryCategory?.id
        };

        const operation = this.assignmentMode === 'replace' ?
            this.templateCategoriesService.assignTemplateToCategories(this.data.templateId, assignmentData) :
            this.templateCategoriesService.assignTemplateToCategories(this.data.templateId, assignmentData);

        operation.subscribe({
            next: (response) => {
                this.isSubmitting = false;
                if (response.errors === false) {
                    this.dialogRef.close({
                        success: true,
                        data: response.data,
                        assignedCategories: this.selectedCategories,
                        primaryCategory: this.primaryCategory
                    });
                } else {
                    this.validationErrors = response.errors.message || ['A apărut o eroare la atribuirea categoriilor.'];
                }
            },
            error: (error) => {
                this.isSubmitting = false;
                console.error('Error assigning categories:', error);

                if (error.error && error.error.errors) {
                    if (error.error.errors.message) {
                        this.validationErrors = Array.isArray(error.error.errors.message)
                            ? error.error.errors.message
                            : [error.error.errors.message];
                    } else {
                        this.validationErrors = ['A apărut o eroare la atribuirea categoriilor.'];
                    }
                } else {
                    this.validationErrors = ['A apărut o eroare neașteptată.'];
                }
            }
        });
    }

    onCancel(): void {
        this.dialogRef.close({ success: false });
    }

    // Bulk operations
    selectAllCategories(): void {
        this.selectedCategories = [...this.availableCategories.filter(c => c.active)];
    }

    clearAllCategories(): void {
        this.selectedCategories = [];
        this.primaryCategory = null;
        this.assignmentForm.patchValue({ set_primary: false });
    }

    // Helper methods
    getCategoryPath(category: TemplateCategory): string {
        return this.templateCategoriesService.getCategoryPath(category, this.availableCategories);
    }

    trackCategory = (index: number, category: TemplateCategory): number => {
        return category.id;
    };

    get selectedCategoryIds(): number[] {
        return this.selectedCategories.map(c => c.id);
    }

    get setPrimaryValue(): boolean {
        return this.assignmentForm.get('set_primary')?.value || false;
    }

    get replaceExistingValue(): boolean {
        return this.assignmentForm.get('replace_existing')?.value || false;
    }

    getCategoryIcon(category: TemplateCategory): string {
        if (category.parent_category_id) {
            return 'label';
        }
        return 'folder';
    }

    hasChanges(): boolean {
        const currentCategoryIds = this.selectedCategories.map(c => c.id).sort();
        const originalCategoryIds = (this.data.currentCategories || []).map(c => c.id).sort();

        const categoriesChanged = JSON.stringify(currentCategoryIds) !== JSON.stringify(originalCategoryIds);

        const originalPrimaryId = this.data.currentPrimaryCategory?.id || null;
        const currentPrimaryId = this.primaryCategory?.id || null;
        const primaryChanged = originalPrimaryId !== currentPrimaryId;

        return categoriesChanged || primaryChanged;
    }

    getAssignmentSummary(): string {
        const count = this.selectedCategories.length;
        const primaryText = this.primaryCategory ? ` (${this.primaryCategory.name} - primară)` : '';

        if (count === 0) {
            return 'Nicio categorie selectată';
        } else if (count === 1) {
            return `1 categorie selectată${primaryText}`;
        } else {
            return `${count} categorii selectate${primaryText}`;
        }
    }
}